import {isFilled, KeyTextField} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next'
import {PrismicRichText} from '@prismicio/react'
import Link from 'next/link'
import {useRouter} from 'next/router'
import {useLinkResolver} from 'prismicio'
import {CustomerStoryDocument} from 'prismicio-types'
import {memo} from 'react'
import {CATEGORIES, LocalesType} from 'src/common/constants'
import {Icon} from 'src/common/Icon'
import {EVENTS, track} from 'src/common/helpers'

type LinkResolverObject = {
  pathname: string
  query: {
    [k: string]: string
  }
}

const getUrl = (href: LinkResolverObject) => {
  const queryString = new URLSearchParams(href.query).toString()
  return `${href.pathname}${queryString ? `?${queryString}` : ''}`
}

export const CustomerStoryCard = memo(function Card(props: {
  customerStory: CustomerStoryDocument
  readMoreLabel: KeyTextField
  index: number
  pageUid: string
}) {
  const {linkResolver} = useLinkResolver()
  const link = linkResolver(props.customerStory)
  const url = getUrl(link)
  const {locale} = useRouter()

  return (
    <Link
      className="col-span-full flex flex-col gap-4 rounded-lg bg-moss-white p-4 active:bg-beige-300 md:col-span-6 md:gap-6 md:p-6 lg:col-span-4"
      href={url}
      locale={props.customerStory.lang || 'en-us'}
      onClick={() =>
        track({
          event: EVENTS.CLICK,
          mktgId: `customer-story-read-more-button`,
          location: `component-${props.index}-customer-story`,
          pageName: props.pageUid,
          dataType: 'customer-story-button',
        })
      }
    >
      <div className="relative w-full rounded-[4px] lg:max-h-[238px]">
        <PrismicNextImage
          className="w-full rounded-[4px] lg:max-h-[217px]"
          field={props.customerStory.data.image}
          priority
        />
        {props.customerStory.data.category && (
          <span className="text-button-s absolute left-3 top-3 rounded-[4px] bg-moss-white/[30%] px-2 py-1 text-moss-white backdrop-blur-[10px]">
            {CATEGORIES[props.customerStory.data.category][locale as LocalesType]}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <PrismicNextImage field={props.customerStory.data.company_icon} className="h-8 w-8" />
            <span className="text-16 font-medium leading-5 text-black/[60%]">
              {props.customerStory.data.company_name}
            </span>
          </div>
          <PrismicRichText
            field={props.customerStory.data.title}
            components={{
              heading1: ({children}) => (
                <span className="truncate-3-lines h-[90px] text-24 leading-[30px] text-black">{children}</span>
              ),
            }}
          />
        </div>
        {isFilled.keyText(props.readMoreLabel) && (
          <div className="group relative flex h-[38px] w-fit items-center gap-[14px] rounded-[4px] border border-moss-grey-150 bg-moss-white pl-[14px] pr-[7px] hover:bg-moss-grey-100">
            <span className="text-button-s uppercase text-black">{props.readMoreLabel}</span>
            <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-green-neon group-hover:bg-green-neon/[80%]">
              <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
            </div>
          </div>
        )}
      </div>
    </Link>
  )
})
